import { useState } from "react";
import cn from "classnames";
import { Icon, IconSize } from "../Icon/Icon";
import { InviteStatusIcon, RSVPStatusIcon, SportIcon } from "../Icon/Icon";
import { Text } from "../Text/Text";
import { Image } from "../Image/Image";
import Styles from "./Avatar.module.scss";
import { ActivityIndicator } from "../ActivityIndicator/ActivityIndicator";
import { FontSize } from "src/components/types";

const LoadingAvatar = ({ size, className }: { size: IconSize; className?: string }) => (
  <div className={cn(Styles.container, Styles.whiteBackground, Styles[size], className)}>
    <div className={cn(Styles.centered)}>
      <ActivityIndicator.Spinner />
    </div>
  </div>
);

export type AccessoryIcon = RSVPStatusIcon | InviteStatusIcon;
export enum AvatarTypeOptions {
  Person = "Person",
  Team = "Team",
}

/**
 * Treats a source that fails to load (e.g. the avatar endpoint 404s for a team
 * with no stored image) as if there were no source, so the caller renders its
 * fallback. Keyed on the source so it resets when the source changes.
 */
const useImageFallback = (source?: string) => {
  const [failedSource, setFailedSource] = useState<string>();
  return {
    hasSource: !!source && source !== failedSource,
    onError: () => setFailedSource(source),
  };
};

const sizeToFontSize = (size: IconSize): FontSize => {
  switch (size) {
    case "small":
      return "small";
    case "medium":
      return "base";
    case "large":
      return "xlarge";
    default:
      return "base";
  }
};

export const getInitials = ({
  firstName,
  lastName,
  email,
}: {
  firstName?: string;
  lastName?: string | null;
  email?: string;
}) => {
  if (firstName && lastName) {
    return firstName.substring(0, 1) + lastName.substring(0, 1);
  }
  if (firstName) {
    return firstName.substring(0, 2);
  }
  if (lastName) {
    return lastName.substring(0, 2);
  }
  if (email) {
    return email.substring(0, 2);
  }

  return "--";
};

interface PersonAvatarProps {
  source?: string;
  size?: IconSize;
  initials?: string;
  accessoryIcon?: AccessoryIcon;
  className?: string;
}

const PersonAvatar = ({ source, size = "small", initials, accessoryIcon, className }: PersonAvatarProps) => {
  const { hasSource, onError } = useImageFallback(source);
  const container = cn(Styles.container, Styles.greyBackground, Styles[size], className);
  const text = cn(Styles.text);
  const accessory = cn(Styles.statusAccessory);
  const centered = cn(Styles.centered);
  return (
    <div className={container}>
      <div className={centered}>
        <div className={text}>
          {" "}
          {!hasSource && (
            <Text size={sizeToFontSize(size)} weight={"bold"} color={"white"}>
              {initials}
            </Text>
          )}
        </div>
        {hasSource && <Image roundedCircle source={source} onError={onError} />}
      </div>
      <div className={accessory}> {accessoryIcon && <Icon size={"xsmall"} type={accessoryIcon} />} </div>
    </div>
  );
};

interface TeamAvatarProps {
  source?: string;
  size?: IconSize;
  sportIcon: SportIcon;
  className?: string;
  altText?: string;
  hideSportIcon?: boolean;
}

const iconSize = (avatarSize: IconSize, isAccessory: boolean): IconSize => {
  if (!isAccessory) {
    return avatarSize;
  }

  switch (avatarSize) {
    case "large":
      return "small";
    default:
      return "xsmall";
  }
};

const TeamAvatar = ({
  source,
  size = "small",
  sportIcon,
  className,
  altText,
  hideSportIcon = false,
}: TeamAvatarProps) => {
  const { hasSource, onError } = useImageFallback(source);
  const container = cn(Styles.container, Styles.whiteBackground, Styles[size], className);
  const centered = cn(Styles.centered);
  const icon = <Icon fill={"offBlack"} size={iconSize(size, hasSource)} type={sportIcon} />;
  if (!hasSource) {
    return (
      <div className={container}>
        <div className={centered}>{icon}</div>
      </div>
    );
  }

  const borderSize = size === "large" ? "smallBorder" : "xsmallBorder";
  const accessory = cn(Styles.sportAccessory, Styles.whiteBackground, Styles[borderSize], {
    [Styles.hideSportIcon]: hideSportIcon,
  });

  return (
    <div className={container}>
      <div className={centered}>
        <Image roundedCircle source={source} altText={altText} onError={onError} />
      </div>
      <div className={accessory}>{sportIcon && icon}</div>
    </div>
  );
};

export const Avatar = {
  Team: TeamAvatar,
  Person: PersonAvatar,
  Loading: LoadingAvatar,
};
